import {
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowDown, ArrowUp, Plus, TrashSimple } from '@phosphor-icons/react';
import { useForm } from 'react-typed-form';
import { useParams } from 'react-router';
import PublishingSection from '../../components/PublishingSection';
import { UnsavedChangesBlocker } from '../../components';
import { usePublishing } from '../../hooks';
import { Api, useApiCallable } from '../../api';
import {
  FieldMediaUpload,
  FieldLargeMediaUpload,
  FieldRadioGroup,
  FieldTextRequired,
  FieldNumber,
} from '../../form';
import TextSlideForm from './story/TextSlideForm';
import TextImageSlideForm from './story/TextImageSlideForm';
import ImageSlideForm from './story/ImageSlideForm';
import { Slide } from './story/types';
import { Model } from '../../models';
import FactSlideForm from './story/FactSlideForm';

type Props = {
  existing?: Api.Lesson.V_DetailAsAdmin;
};

export default function LessonForm({ existing }: Props) {
  const callApi = useApiCallable();
  const { weekId } = useParams();

  const publishing = usePublishing(
    {
      contentType: 'Lesson',
      apiPath: 'lessons',
      existingId: existing?.id,
    },
    () => form.handleSubmit(),
  );

  const existingSlides =
    existing?.slides?.map((slide) => ({
      templateName: slide.templateName,
      templateData: Model.Lesson.parseSlideTemplateData(
        slide.templateName,
        slide.templateData as string[],
      ),
      position: slide.position,
    })) || [];

  const initialValues = {
    title: existing?.title ?? '',
    position: existing?.position ?? null,
    thumb: existing?.thumb?.['@id'] ?? null,
    type: existing?.video ? 'video' : 'story',
    video: existing?.video?.['@id'] ?? null,
    slides:
      existingSlides.length > 0
        ? existingSlides
        : [
            {
              templateName: 'text-image',
              templateData: {
                title: '',
                markdown: '',
                background_image_id: null,
                image_id: null,
                add_question: false,
                question: '',
              },
              position: 0,
            },
          ],
    week: `/program-weeks/${weekId}`,
  };

  const form = useForm({
    defaultValues: initialValues,
    preValidateTransform: (values) => ({
      ...values,
      slides: values.slides.map((slide) => ({
        ...slide,
        templateData: {
          ...slide.templateData,
          question: slide.templateData.add_question
            ? slide.templateData.question
            : '',
        },
      })),
    }),
    onSubmit: async (values, form) => {
      const { setLoading, addSubmitError } = form;
      let response;

      const apiSlides = values.slides.map((slide) => ({
        templateName: slide.templateName,
        templateData: Model.Lesson.serializeSlideTemplateData(
          slide.templateName,
          slide.templateData,
        ),
        position: slide.position,
      }));

      const submitValues = {
        ...values,
        position: values.position,
        slides: apiSlides,
      };

      if (!existing) {
        setLoading(true);
        response = await callApi(
          `/lessons`,
          {
            jsonBody: submitValues,
            method: 'POST',
          },
          {
            addSubmitError,
          },
        );
      } else {
        response = await callApi(
          `/lessons/${existing.id}`,
          {
            method: 'PUT',
            jsonBody: submitValues,
          },
          {
            addSubmitError,
          },
        );
      }

      setLoading(false);

      if (!response) return;

      await publishing.handleSubmitSuccess(response, !!existing);
    },
  });

  const addSlide = () => {
    const slides = [...form.values.slides];
    slides.push({
      templateName: 'text-image',
      templateData: {
        title: '',
        markdown: '',
        background_image_id: null,
        image_id: null,
        add_question: false,
        question: '',
      },
      position: slides.length,
    });
    form.getField('slides').handleValueChange(slides);
  };

  const moveSlide = (fromIndex: number, toIndex: number) => {
    const slides = [...form.values.slides];
    const [removed] = slides.splice(fromIndex, 1);
    slides.splice(toIndex, 0, removed);
    slides.forEach((slide, index) => (slide.position = index));
    form.getField('slides').handleValueChange(slides);
  };

  const deleteSlide = (index: number) => {
    const slides = [...form.values.slides];
    slides.splice(index, 1);
    slides.forEach((slide, index) => (slide.position = index));
    form.getField('slides').handleValueChange(slides);
  };

  const updateSlideField = (index: number, field: keyof Slide, value: any) => {
    const slides = [...form.values.slides];
    slides[index] = { ...slides[index], [field]: value };
    form.getField('slides').handleValueChange(slides);
  };

  const updateSlideTemplateField = (
    index: number,
    field: keyof Slide['templateData'],
    value: any,
  ) => {
    const slides = [...form.values.slides];
    slides[index].templateData = {
      ...slides[index].templateData,
      [field]: value,
    };
    form.getField('slides').handleValueChange(slides);
  };

  console.log(existing);

  return (
    <Stack gap={3}>
      <Stack gap={3} maxWidth={600}>
        <FieldTextRequired
          field={form.getField('title')}
          label="Title"
          helperText="The title will be displayed on the app"
        />
        <FieldNumber
          field={form.getField('position')}
          label="Position Number"
          helperText="The position of this lesson in the lessons list"
          required
        />
        <FieldMediaUpload
          field={form.getField('thumb')}
          label="Thumbnail"
          acceptedTypes={['IMAGE_TYPE']}
          helperText="2:3 aspect ratio"
          existingAsset={existing?.thumb}
          aspectRatio="2/3"
          required
        />
        <FieldRadioGroup
          field={form.getField('type')}
          options={[
            { label: 'Story', value: 'story' },
            { label: 'Video', value: 'video' },
          ]}
          label="Type"
          row
          required
        />
      </Stack>
      <Divider />
      {form.values.type === 'story' && (
        <Stack gap={3}>
          <Typography variant="h6">Story Slides</Typography>
          {form.values.slides.map((slide, index) => {
            const hasQuestionInOtherSlide = form.values.slides.some(
              (s, i) => i !== index && s.templateData.add_question,
            );
            return (
              <Card key={`slide-${index}`} sx={{ p: 2, maxWidth: 960 }}>
                <Stack gap={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Chip label={index + 1} size="medium" color="secondary" />
                      <IconButton
                        disabled={form.values.slides.length === 1}
                        onClick={() => deleteSlide(index)}
                        color="primary"
                      >
                        <TrashSimple />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" gap={2}>
                      <Button
                        disabled={index === 0}
                        onClick={() => moveSlide(index, index - 1)}
                        startIcon={<ArrowUp />}
                        variant="text"
                        color="primary"
                      >
                        Move up
                      </Button>
                      <Button
                        disabled={index === form.values.slides.length - 1}
                        onClick={() => moveSlide(index, index + 1)}
                        startIcon={<ArrowDown />}
                        variant="text"
                        color="primary"
                      >
                        Move down
                      </Button>
                    </Stack>
                  </Stack>
                  <FieldRadioGroup
                    field={{
                      name: 'templateName',
                      label: 'Slide Type',
                      value: slide.templateName,
                      handleValueChange: (value: string) =>
                        updateSlideField(
                          index,
                          'templateName',
                          value as Slide['templateName'],
                        ),
                    }}
                    options={Model.Lesson.SLIDE_TYPES}
                    row
                  />
                  {slide.templateName === 'text-image' && (
                    <TextImageSlideForm
                      slide={slide}
                      onUpdateField={(field, value) =>
                        updateSlideTemplateField(index, field, value)
                      }
                      hasQuestionInOtherSlide={hasQuestionInOtherSlide}
                    />
                  )}
                  {slide.templateName === 'text' && (
                    <TextSlideForm
                      slide={slide}
                      onUpdateField={(field, value) =>
                        updateSlideTemplateField(index, field, value)
                      }
                      hasQuestionInOtherSlide={hasQuestionInOtherSlide}
                    />
                  )}
                  {slide.templateName === 'fact' && (
                    <FactSlideForm
                      slide={slide}
                      onUpdateField={(field, value) =>
                        updateSlideTemplateField(index, field, value)
                      }
                    />
                  )}
                  {slide.templateName === 'image' && (
                    <ImageSlideForm
                      slide={slide}
                      onUpdateField={(field, value) =>
                        updateSlideTemplateField(index, field, value)
                      }
                    />
                  )}
                </Stack>
              </Card>
            );
          })}
          <Button
            startIcon={<Plus />}
            variant="outlined"
            onClick={addSlide}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Slide
          </Button>
        </Stack>
      )}
      {form.values.type === 'video' && (
        <Stack gap={3}>
          <Typography variant="h6">Video</Typography>
          <FieldLargeMediaUpload
            field={form.getField('video')}
            label="Video File"
            acceptedTypes={['VIDEO_TYPE']}
            existingAsset={existing?.video}
            helperText="9:16 aspect radio"
            required
          />
        </Stack>
      )}
      <PublishingSection
        existing={existing}
        isLoading={form.isLoading}
        contentType="Lesson"
        apiPath="lessons"
        publishing={publishing}
      />

      <UnsavedChangesBlocker
        initialValues={initialValues}
        currentValues={form.values}
        isSubmittingRef={publishing.isSubmittingRef}
      />
    </Stack>
  );
}
